<template>
  <Page>
    <div class="flex-1 rounded-lg bg-background-card shadow-card overflow-hidden flex flex-col h-full">
      <!-- Panel Header -->
      <div class="bg-primary/10 px-4 py-1.5 border-b border-border flex shrink-0 items-center justify-between min-h-12">
        <DocumentManagerToolbar
          v-model:search-term="searchTerm"
          v-model:multi-select-enabled="multiSelectEnabled"
          v-model:status-filter="statusFilter"
          :selection="selectedDocuments"
          :loading="loading"
          :document-count="page.totalItems ?? documents.length"
          @search="onSearch"
          @update:status-filter="onStatusFilterChange"
          @add="createModalVisible = true"
          @bulk-add="bulkCreateModalVisible = true"
          @associate="assetPickerVisible = true"
          @dissociate="dissociateModalVisible = true"
          @delete="confirmBulkDelete"
        />
      </div>

      <!-- Panel Body -->
      <div v-if="loading && !documents.length" class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light">
        Loading documents…
      </div>

      <div v-else-if="error" class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-error">
        {{ error }}
      </div>

      <div v-else-if="!documents.length" class="flex flex-1 items-center justify-center px-6 py-10">
        <div class="max-w-md text-center">
          <h2 class="text-base font-semibold text-text">No documents found</h2>
          <p class="mt-1 text-sm text-text-light">
            {{ hasActiveFilters ? 'Try adjusting or clearing your search.' : 'Add a document to get started.' }}
          </p>
          <button v-if="!hasActiveFilters" class="btn-primary mt-4" type="button" @click="createModalVisible = true">
            <i class="fa-solid fa-plus mr-2" aria-hidden="true"></i>
            Add Document
          </button>
        </div>
      </div>

      <div v-else class="flex-1 min-h-0 flex flex-col overflow-hidden">
        <DocumentManagerTable
          v-model:selection="selectedDocuments"
          v-model:expanded-rows="expandedRows"
          :rows="documents"
          :loading="loading"
          :sort-field="sortState.field"
          :sort-order="sortState.order"
          :multi-select-enabled="multiSelectEnabled"
          :expanded-assets="expandedAssets"
          :expanded-loading="expandedLoading"
          @sort="onSort"
          @row-expand="onRowExpand"
          @row-collapse="onRowCollapse"
          @download="handleDownload"
          @delete-row="confirmSingleDelete"
          @restore-row="confirmRestore"
        />
        <Paginator
          class="shrink-0"
          dense
          :current-page="page.page"
          :page-count="pageCount"
          :page-size="page.pageSize"
          :total-items="page.totalItems ?? documents.length"
          item-label="documents"
          @update:currentPage="onPageChange"
          @first="() => onPageChange(1)"
          @prev="() => onPageChange(Math.max(1, page.page - 1))"
          @next="() => onPageChange(Math.min(pageCount, page.page + 1))"
          @last="() => onPageChange(pageCount)"
        />
      </div>
    </div>

    <DocumentManagerCreateDocumentModal
      :visible="createModalVisible"
      @update:visible="createModalVisible = $event"
      @saved="handleDocumentCreated"
    />

    <BulkCreateDocumentsModal
      :visible="bulkCreateModalVisible"
      @update:visible="bulkCreateModalVisible = $event"
      @saved="handleDocumentCreated"
    />

    <AssetPickerModal
      :visible="assetPickerVisible"
      :loading="isProcessing"
      @update:visible="assetPickerVisible = $event"
      @confirm="handleBulkAssociate"
    />

    <DissociateAssetsModal
      :visible="dissociateModalVisible"
      :document-ids="selectedDocumentIds"
      :document-count="selectedDocuments.length"
      :loading="isProcessing"
      @update:visible="dissociateModalVisible = $event"
      @confirm="handleBulkDissociate"
    />
  </Page>
</template>

<script setup lang="ts">
import { computed, onActivated, onMounted, ref } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { addToast } from '@/shared/utils/toastMessages'
import Page from '@/shared/components/layouts/Page.vue'
import Paginator from '@/shared/components/ui/Paginator.vue'
import DocumentManagerToolbar from '../components/DocumentManagerToolbar.vue'
import DocumentManagerTable from '../components/DocumentManagerTable.vue'
import DocumentManagerCreateDocumentModal from '../components/Modals/DocumentManagerCreateDocumentModal.vue'
import BulkCreateDocumentsModal from '../components/Modals/BulkCreateDocumentsModal.vue'
import AssetPickerModal from '../components/Modals/AssetPickerModal.vue'
import DissociateAssetsModal from '../components/Modals/DissociateAssetsModal.vue'
import { useDocumentManager } from '../composables/useDocumentManager'
import { useBulkAssociation } from '../composables/useBulkAssociation'
import { DocumentsService } from '@/shared/services/documents'
import type { DocumentWithAssetsDto } from '../types'

definePage({
  meta: {
    title: 'Document Manager',
  },
})

const toast = useToast()
const confirm = useConfirm()

const {
  documents,
  loading,
  error,
  searchTerm,
  selectedDocuments,
  multiSelectEnabled,
  statusFilter,
  expandedRows,
  expandedAssets,
  expandedLoading,
  page,
  sortState,
  pageCount,
  hasActiveFilters,
  fetchDocuments,
  onSort,
  onPageChange,
  onSearch,
  onStatusFilterChange,
  onRowExpand,
  onRowCollapse,
  clearExpandedCache,
} = useDocumentManager()

const { isProcessing, bulkAssociate, bulkDissociate } = useBulkAssociation()

const createModalVisible = ref(false)
const bulkCreateModalVisible = ref(false)
const assetPickerVisible = ref(false)
const dissociateModalVisible = ref(false)

const selectedDocumentIds = computed(() =>
  selectedDocuments.value.map((d) => d.id)
)

onMounted(() => {
  void fetchDocuments()
})

onActivated(() => {
  void fetchDocuments()
})

function handleDocumentCreated() {
  clearExpandedCache()
  void fetchDocuments()
}

async function handleBulkAssociate(assetIds: string[]) {
  const result = await bulkAssociate(selectedDocumentIds.value, assetIds)
  addToast(toast, {
    severity: result.success ? 'success' : 'error',
    summary: result.success ? 'Association Complete' : 'Association Failed',
    detail: result.message,
  })
  if (result.success) {
    assetPickerVisible.value = false
    selectedDocuments.value = []
    clearExpandedCache()
    void fetchDocuments()
  }
}

async function handleBulkDissociate(assetIds: string[]) {
  const result = await bulkDissociate(selectedDocumentIds.value, assetIds)
  addToast(toast, {
    severity: result.success ? 'success' : 'error',
    summary: result.success ? 'Dissociation Complete' : 'Dissociation Failed',
    detail: result.message,
  })
  if (result.success) {
    dissociateModalVisible.value = false
    selectedDocuments.value = []
    clearExpandedCache()
    void fetchDocuments()
  }
}

async function handleDownload(doc: DocumentWithAssetsDto) {
  try {
    const data = await DocumentsService.downloadContent(doc.id, { responseType: 'blob' })
    const blob = data instanceof Blob ? data : new Blob([data as ArrayBuffer])
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = doc.filePath ?? doc.documentId ?? 'download'
    a.click()
    URL.revokeObjectURL(url)
  } catch {
    addToast(toast, {
      severity: 'error',
      summary: 'Download Failed',
      detail: 'Unable to download document content.',
    })
  }
}

function confirmSingleDelete(doc: DocumentWithAssetsDto) {
  confirm.require({
    message: `Delete document "${doc.documentId ?? doc.id}"?`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await DocumentsService.delete(doc.id)
        addToast(toast, { severity: 'success', summary: 'Deleted', detail: 'Document deleted.' })
        clearExpandedCache()
        void fetchDocuments()
      } catch {
        addToast(toast, { severity: 'error', summary: 'Error', detail: 'Failed to delete document.' })
      }
    },
  })
}

function confirmRestore(doc: DocumentWithAssetsDto) {
  confirm.require({
    message: `Restore document "${doc.documentId ?? doc.id}"?`,
    header: 'Confirm Restore',
    icon: 'pi pi-info-circle',
    accept: async () => {
      try {
        await DocumentsService.restore(doc.id)
        addToast(toast, { severity: 'success', summary: 'Restored', detail: 'Document restored.' })
        clearExpandedCache()
        void fetchDocuments()
      } catch {
        addToast(toast, { severity: 'error', summary: 'Restore Failed', detail: 'Failed to restore document.' })
      }
    },
  })
}

function confirmBulkDelete() {
  const activeSelected = selectedDocuments.value.filter((doc) => doc.isActive)

  if (activeSelected.length === 0) {
    addToast(toast, {
      severity: 'warn',
      summary: 'No Active Documents',
      detail: 'Delete applies to active documents only.',
    })
    return
  }

  confirm.require({
    message: `Delete ${activeSelected.length} selected document(s)?`,
    header: 'Confirm Bulk Delete',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      let deleted = 0
      for (const doc of activeSelected) {
        try {
          await DocumentsService.delete(doc.id)
          deleted++
        } catch {
          // continue with remaining
        }
      }
      addToast(toast, {
        severity: deleted > 0 ? 'success' : 'error',
        summary: deleted > 0 ? 'Deleted' : 'Error',
        detail: `${deleted} of ${activeSelected.length} document(s) deleted.`,
      })
      selectedDocuments.value = []
      clearExpandedCache()
      void fetchDocuments()
    },
  })
}
</script>
